// src/components/Footer.jsx
import React, { useEffect, useState, useRef } from "react";
import { Link, NavLink } from "react-router-dom";
import { useTranslation } from "../i18n";

/* ═══════════════════════════════════════════
   STATIC DATA
═══════════════════════════════════════════ */
const SOCIALS = [
  { icon: "bi-twitter-x",  href: "https://x.com/0news_247969",   labelKey: "twitter_label"   },
  { icon: "bi-facebook",   href: "https://www.facebook.com/profile.php?id=61579447607022",  labelKey: "facebook_label"  },
  { icon: "bi-instagram",  href: "https://www.instagram.com/24.0_news/", labelKey: "instagram_label" },
  { icon: "bi-youtube",    href: "https://youtube.com/@news24-now?si=SRo_3hctE-kQBzZr",   labelKey: "youtube_label"   },
];

/* Uses the exact keys that exist in en.json */
const NAV_COLS = [
  {
    headingKey: "explore_heading",
    links: [
      { labelKey: "home",     to: "/"                 },
      { labelKey: "news",     to: "/category/news"    },
      { labelKey: "tech",     to: "/category/tech"    },
      { labelKey: "blog",     to: "/blog"             },
      { labelKey: "authors",  to: "/authors"          },
      { labelKey: "contact",  to: "/contact"          },
    ],
  },
  {
    headingKey: "subjects",
    links: [
      { labelKey: "category_world",    to: "/category/world"    },
      { labelKey: "category_politics", to: "/category/politics" },
      { labelKey: "category_tech",     to: "/category/tech"     },
      { labelKey: "category_startups", to: "/category/startups" },
      { labelKey: "category_sport",    to: "/category/sport"    },
      { labelKey: "category_culture",  to: "/category/culture"  },
    ],
  },
];

const TOPIC_CHIPS = [
  { labelKey: "category_world",    to: "/category/world"    },
  { labelKey: "category_politics", to: "/category/politics" },
  { labelKey: "category_tech",     to: "/category/tech"     },
  { labelKey: "category_startups", to: "/category/startups" },
  { labelKey: "category_sport",    to: "/category/sport"    },
  { labelKey: "category_culture",  to: "/category/culture"  },
];

const LEGAL = [
  { labelKey: "privacy", to: "/privacy" },
  { labelKey: "terms",   to: "/terms"   },
  { labelKey: "cookies", to: "/cookies" },
  { labelKey: "sitemap", to: "/sitemap" },
];

const STATUS = { IDLE: "idle", LOADING: "loading", SUCCESS: "success", ERROR: "error" };

/* ═══════════════════════════════════════════
   FOOTER
═══════════════════════════════════════════ */
const Footer = () => {
  const { lang = "en", t } = useTranslation();
  const isRTL = ["ar", "he", "fa", "ur"].includes(lang);
  const year  = new Date().getFullYear();

  const [email,   setEmail]   = useState("");
  const [status,  setStatus]  = useState(STATUS.IDLE);
  const [showTop, setShowTop] = useState(false);
  const [mounted, setMounted] = useState(false);

  /* scroll watcher */
  useEffect(() => {
    const fn = () => setShowTop(window.scrollY > 400);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  /* mount animation */
  useEffect(() => {
    const id = setTimeout(() => setMounted(true), 60);
    return () => clearTimeout(id);
  }, []);

  const toTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || status === STATUS.LOADING) return;
    setStatus(STATUS.LOADING);
    try {
      // TODO: wire to your newsletter endpoint
      await new Promise((r) => setTimeout(r, 900));
      setStatus(STATUS.SUCCESS);
      setEmail("");
    } catch {
      setStatus(STATUS.ERROR);
    }
  };

  /* ── RENDER ── */
  return (
    <>
      <FooterStyles />

      <footer
        className={[
          "ft-root",
          isRTL   ? "ft-rtl" : "",
          mounted ? "ft-in"  : "",
        ].filter(Boolean).join(" ")}
        dir={isRTL ? "rtl" : "ltr"}
      >
        {/* Red top accent line */}
        <div className="ft-top-rule" aria-hidden />

        {/* ════════════════════════════════
            NEWSLETTER BAND
        ════════════════════════════════ */}
        <div className="ft-nl-band">
          <div className="ft-container ft-nl-inner">

            {/* Copy */}
            <div className="ft-nl-copy">
              <span className="ft-nl-badge">
                <span className="ft-nl-dot" aria-hidden />
                {t("newsletter_badge")}
              </span>

              {/* ── EXACT three-part heading from your JSON ── */}
              <h2 className="ft-nl-heading" dir={isRTL ? "rtl" : "ltr"}>
                <span className="ft-nl-h-lead">
                  {t("newsletter_heading_lead")}
                </span>{" "}
                <span className="ft-nl-h-highlight">
                  {t("newsletter_heading_highlight")}
                </span>{" "}
                <span className="ft-nl-h-tail">
                  {t("newsletter_heading_tail")}
                </span>
              </h2>

              <p className="ft-nl-sub">
                {/* newsletter_subtext exists in your JSON */}
                {t("newsletter_subtext")}
              </p>
            </div>

            {/* Form */}
            <div className="ft-nl-form-wrap">
              {status === STATUS.SUCCESS ? (
                <div className="ft-nl-success" role="status">
                  <span className="ft-nl-success-ring">
                    <i className="bi bi-check-lg" />
                  </span>
                  <span>You're subscribed! Check your inbox.</span>
                </div>
              ) : (
                <form className="ft-nl-form" onSubmit={onSubmit} noValidate>
                  <div className="ft-nl-input-row">
                    <label htmlFor="ft-email" className="ft-sr-only">
                      {t("email_placeholder")}
                    </label>
                    <input
                      id="ft-email"
                      type="email"
                      required
                      className="ft-nl-input"
                      /* email_placeholder key exists in your JSON */
                      placeholder={t("email_placeholder")}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      aria-label={t("email_aria")}
                      disabled={status === STATUS.LOADING}
                      dir="ltr"
                    />
                    <button
                      type="submit"
                      className={`ft-nl-btn${status === STATUS.LOADING ? " ft-nl-btn--loading" : ""}`}
                      disabled={status === STATUS.LOADING}
                    >
                      {status === STATUS.LOADING
                        ? <span className="ft-spinner" aria-hidden />
                        : (
                          <>
                            {t("subscribe")}
                            <i className={`bi ${isRTL ? "bi-arrow-left" : "bi-arrow-right"}`} aria-hidden />
                          </>
                        )
                      }
                    </button>
                  </div>

                  {status === STATUS.ERROR && (
                    <p className="ft-nl-err" role="alert">
                      <i className="bi bi-exclamation-circle" />
                      Something went wrong. Please try again.
                    </p>
                  )}

                  {/* by_subscribing_prefix / privacy_policy / by_subscribing_suffix — exact keys */}
                  <p className="ft-nl-privacy">
                    <i className="bi bi-shield-check" aria-hidden />
                    {t("by_subscribing_prefix")}&nbsp;
                    <Link to="/privacy" className="ft-nl-privacy-link">
                      {t("privacy_policy")}
                    </Link>
                    {t("by_subscribing_suffix")}
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>

        {/* ════════════════════════════════
            MAIN LINK GRID
        ════════════════════════════════ */}
        <div className="ft-main">
          <div className="ft-container ft-main-grid">

            {/* Brand column */}
            <div className="ft-brand-col">
              <Link to="/" className="ft-brand" aria-label={t("logo_aria")}>
                <svg width="30" height="30" viewBox="0 0 32 32" fill="none" className="ft-logo" aria-hidden>
                  <rect width="32" height="32" rx="7" fill="#e63311"/>
                  <path d="M6 9h4.5l8 10.5V9h4.5v14h-4.5L10.5 12.5V23H6V9z" fill="white"/>
                </svg>
                <span className="ft-brand-name">
                  {/* site_name_prefix + site_name_highlight — exact keys */}
                  <span className="ft-brand-num">{t("site_name_prefix")}</span>
                  {t("site_name_highlight")}
                </span>
              </Link>

              {/* brand_description — exact key */}
              <p className="ft-brand-desc">{t("brand_description")}</p>

              {/* Social icons */}
              <div className="ft-socials" role="list" aria-label={t("follow_us")}>
                {SOCIALS.map(({ icon, href, labelKey }) => (
                  <a
                    key={icon}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ft-social"
                    aria-label={t(labelKey)}
                    title={t(labelKey)}
                    role="listitem"
                  >
                    <i className={`bi ${icon}`} />
                  </a>
                ))}
              </div>
              {/* App badges 
              <div className="ft-app-badges">
                <a href="#" className="ft-app-badge" aria-label="Download on the App Store">
                  <i className="bi bi-apple" />
                  <div className="ft-app-badge-text">
                    <span className="ft-app-small">Download on the</span>
                    <span className="ft-app-big">App Store</span>
                  </div>
                </a>
                <a href="#" className="ft-app-badge" aria-label="Get it on Google Play">
                  <i className="bi bi-google-play" />
                  <div className="ft-app-badge-text">
                    <span className="ft-app-small">Get it on</span>
                    <span className="ft-app-big">Google Play</span>
                  </div>
                </a>
              </div>*/}
            </div>

            {/* Nav link columns */}
            {NAV_COLS.map((col) => (
              <div key={col.headingKey} className="ft-nav-col">
                <h3 className="ft-col-heading">{t(col.headingKey)}</h3>
                <ul className="ft-nav-list" role="list">
                  {col.links.map(({ labelKey, to }) => (
                    <li key={labelKey} role="listitem">
                      <NavLink to={to} className="ft-nav-link">
                        {t(labelKey)}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            {/* Topics chips */}
            <div className="ft-nav-col">
              {/* categories_heading — exact key */}
              <h3 className="ft-col-heading">{t("categories_heading")}</h3>
              <div className="ft-topics">
                {TOPIC_CHIPS.map(({ labelKey, to }) => (
                  <Link key={labelKey} to={to} className="ft-topic-chip">
                    #{t(labelKey)}
                  </Link>
                ))}
              </div>
            </div>

            {/* Contact column */}
            <div className="ft-nav-col">
              {/* contact_heading — exact key */}
              <h3 className="ft-col-heading">{t("contact_heading")}</h3>
              <ul className="ft-contact-list" role="list">
                <li>
                  <i className="bi bi-geo-alt ft-contact-icon" aria-hidden />
                  {/* contact_address — exact key */}
                  <span>{t("contact_address")}</span>
                </li>
                <li>
                  <i className="bi bi-envelope ft-contact-icon" aria-hidden />
                  {/* contact_email_value — exact key */}
                  <a href={`mailto:${t("contact_email_value")}`} className="ft-nav-link">
                    {t("contact_email_value")}
                  </a>
                </li>
                
                <li>
                  <i className="bi bi-clock ft-contact-icon" aria-hidden />
                  {/* contact_hours — exact key */}
                  <span>{t("contact_hours")}</span>
                </li>
              </ul>

              <div className="ft-live-badge">
                <span className="ft-live-dot" aria-hidden />
                {t("live")} · 24/7
              </div>
            </div>

          </div>
        </div>

        {/* ════════════════════════════════
            DIVIDER
        ════════════════════════════════ */}
        <div className="ft-container">
          <div className="ft-divider" aria-hidden />
        </div>

        {/* ════════════════════════════════
            LEGAL BAR
        ════════════════════════════════ */}
        <div className="ft-legal-band">
          <div className="ft-container ft-legal-inner">
            <p className="ft-copyright">
              <span className="ft-copy-sym">©</span>
              {year}&nbsp;
              <Link to="/" className="ft-brand-link">
                {/* site_name_full — exact key */}
                {t("site_name_full")}
              </Link>
              &nbsp;·&nbsp;
              {/* all_rights_reserved — exact key */}
              {t("all_rights_reserved")}
            </p>

            <nav className="ft-legal-links" aria-label="Legal">
              {LEGAL.map(({ labelKey, to }) => (
                <Link key={labelKey} to={to} className="ft-legal-link">
                  {t(labelKey)}
                </Link>
              ))}
            </nav>
          </div>
        </div>
      </footer>

      {/* Back to top — back_to_top exact key */}
      
    </>
  );
};

/* ═══════════════════════════════════════════
   STYLES — matches header design system exactly
═══════════════════════════════════════════ */
let _ftInjected = false;
const FooterStyles = () => {
  useEffect(() => {
    if (_ftInjected || document.getElementById("ft-css")) return;
    _ftInjected = true;
    const el = document.createElement("style");
    el.id = "ft-css";
    el.textContent = FT_CSS;
    document.head.appendChild(el);
  }, []);
  return null;
};

const FT_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&family=Noto+Sans+Arabic:wght@400;500;600;700&display=swap');

/* ── Shared header tokens ── */
:root {
  --nh-font:    'Sora','Helvetica Neue',sans-serif;
  --nh-font-ar: 'Noto Sans Arabic','Arabic UI Text',Tahoma,sans-serif;
  --nh-red:     #e63311;
  --nh-red-dk:  #c4280d;
  --nh-ink:     #111;
  --nh-ink2:    #6b7280;
  --nh-bg:      #fff;
  --nh-bg2:     #f7f7f7;
  --nh-border:  rgba(0,0,0,.08);
  --nh-sh-sm:   0 1px 4px rgba(0,0,0,.06);
  --nh-sh-md:   0 8px 24px rgba(0,0,0,.10);
  --nh-sh-lg:   0 24px 60px rgba(0,0,0,.16);
  --nh-r:       10px;
  --nh-t:       0.18s cubic-bezier(.4,0,.2,1);
}

/* ── Footer tokens ── */
:root {
  --ft-nl-bg:    #0f0f0e;
  --ft-main-bg:  #fafaf9;
  --ft-legal-bg: #111;
}

.ft-sr-only{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;}

/* ════════════════════════════════════════
   ROOT
════════════════════════════════════════ */
.ft-root,.ft-root *{box-sizing:border-box;}
.ft-root{font-family:var(--nh-font);background:var(--ft-main-bg);color:var(--nh-ink);}
.ft-rtl{direction:rtl;}
.ft-rtl,.ft-rtl *{font-family:var(--nh-font-ar);}

/* Red accent rule at very top — mirrors header's top strip */
.ft-top-rule{
  height:3px;
  background:linear-gradient(to right, var(--nh-red) 120px, var(--nh-border) 120px);
}
.ft-rtl .ft-top-rule{
  background:linear-gradient(to left, var(--nh-red) 120px, var(--nh-border) 120px);
}

.ft-container{
  width:100%;max-width:1320px;margin:0 auto;
  padding-left:clamp(14px,4vw,32px);
  padding-right:clamp(14px,4vw,32px);
}

/* ════════════════════════════════════════
   NEWSLETTER BAND
════════════════════════════════════════ */
.ft-nl-band{
  background:var(--ft-nl-bg);
  padding:clamp(36px,6vw,72px) 0;
  position:relative;overflow:hidden;
}

/* dot grid texture — same as header */
.ft-nl-band::before{
  content:'';position:absolute;inset:0;
  background-image:radial-gradient(rgba(255,255,255,.03) 1px,transparent 1px);
  background-size:22px 22px;pointer-events:none;
}

/* warm red glow orb */
.ft-nl-band::after{
  content:'';position:absolute;
  top:-80px;right:-80px;
  width:400px;height:400px;border-radius:50%;
  background:radial-gradient(circle, rgba(230,51,17,.15) 0%, transparent 70%);
  pointer-events:none;
}
.ft-rtl .ft-nl-band::after{right:auto;left:-80px;}

.ft-nl-inner{
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:clamp(28px,5vw,72px);
  align-items:center;
  position:relative;z-index:1;
}
@media(max-width:767px){.ft-nl-inner{grid-template-columns:1fr;gap:28px;}}

/* ── copy side ── */
.ft-nl-copy{display:flex;flex-direction:column;}

.ft-nl-badge{
  display:inline-flex;align-items:center;gap:6px;
  width:fit-content;
  background:rgba(230,51,17,.14);color:var(--nh-red);
  border:1px solid rgba(230,51,17,.25);
  font-size:0.6rem;font-weight:700;letter-spacing:.1em;text-transform:uppercase;
  padding:5px 10px;border-radius:3px;
  margin-bottom:14px;font-family:var(--nh-font);
}
.ft-nl-dot{
  width:5px;height:5px;border-radius:50%;
  background:var(--nh-red);flex-shrink:0;
  animation:ft-blink 1.8s ease-in-out infinite;
}
@keyframes ft-blink{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.3;transform:scale(.6)}}

/* Three-part heading — each span styled independently */
.ft-nl-heading{
  font-size:clamp(1.375rem,3.2vw,2.375rem);
  font-weight:800;line-height:1.18;
  letter-spacing:-.03em;
  color:#fff;margin:0 0 14px;
  font-family:var(--nh-font);
}
.ft-rtl .ft-nl-heading{letter-spacing:0;line-height:1.45;}

/* "Stay in the loop." — plain white */
.ft-nl-h-lead{color:#fff;}

/* "Get the best stories" — red accent, same as nh-brand-num */
.ft-nl-h-highlight{
  color:var(--nh-red);
  display:inline;
  /* subtle underline decoration */
  text-decoration:underline;
  text-decoration-color:rgba(230,51,17,.35);
  text-underline-offset:4px;
}

/* "every week." — muted white */
.ft-nl-h-tail{color:rgba(255,255,255,.75);}

.ft-nl-sub{
  font-size:clamp(0.8125rem,1.2vw,0.9375rem);
  color:rgba(255,255,255,.5);line-height:1.7;margin:0;
}

/* ── form side ── */
.ft-nl-form-wrap{position:relative;z-index:1;}

.ft-nl-input-row{
  display:flex;gap:0;
  background:rgba(255,255,255,.07);
  border:1.5px solid rgba(255,255,255,.11);
  border-radius:var(--nh-r);overflow:hidden;
  transition:border-color var(--nh-t),box-shadow var(--nh-t);
}
/* same focus ring as header search bar */
.ft-nl-input-row:focus-within{
  border-color:var(--nh-red);
  box-shadow:0 0 0 3px rgba(230,51,17,.12);
}

.ft-nl-input{
  flex:1;min-width:0;border:none;background:transparent;outline:none;
  font-size:0.875rem;font-family:var(--nh-font);
  color:#fff;padding:13px 15px;
}
.ft-nl-input::placeholder{color:rgba(255,255,255,.3);}

/* subscribe button — same solid style as nh-btn-solid */
.ft-nl-btn{
  display:inline-flex;align-items:center;gap:7px;
  padding:13px 20px;
  background:var(--nh-red);color:#fff;border:none;
  font-size:0.82rem;font-weight:700;font-family:var(--nh-font);
  cursor:pointer;white-space:nowrap;flex-shrink:0;
  min-width:120px;justify-content:center;
  transition:background var(--nh-t),transform .12s;
}
.ft-nl-btn:hover:not(:disabled){background:var(--nh-red-dk);}
.ft-nl-btn:active:not(:disabled){transform:scale(.97);}
.ft-nl-btn--loading,.ft-nl-btn:disabled{cursor:not-allowed;opacity:.75;}

.ft-spinner{
  width:15px;height:15px;border-radius:50%;
  border:2px solid rgba(255,255,255,.25);
  border-top-color:#fff;
  animation:ft-spin .65s linear infinite;
  display:inline-block;
}
@keyframes ft-spin{to{transform:rotate(360deg);}}

.ft-nl-err{
  display:flex;align-items:center;gap:6px;
  margin-top:8px;font-size:0.74rem;color:#f87171;
  font-family:var(--nh-font);
}

.ft-nl-privacy{
  display:flex;align-items:center;gap:5px;flex-wrap:wrap;
  margin-top:10px;font-size:0.69rem;
  color:rgba(255,255,255,.32);font-family:var(--nh-font);
}
.ft-nl-privacy-link{color:rgba(255,255,255,.42);text-decoration:underline;transition:color var(--nh-t);}
.ft-nl-privacy-link:hover{color:rgba(255,255,255,.8);}

/* success */
.ft-nl-success{
  display:flex;align-items:center;gap:14px;
  padding:18px 20px;
  background:rgba(22,163,74,.1);border:1px solid rgba(22,163,74,.22);
  border-radius:var(--nh-r);color:#86efac;
  font-size:0.9375rem;font-weight:600;font-family:var(--nh-font);
}
.ft-nl-success-ring{
  width:36px;height:36px;border-radius:50%;
  border:2px solid rgba(22,163,74,.4);
  display:flex;align-items:center;justify-content:center;
  font-size:0.9rem;flex-shrink:0;color:#4ade80;
}

@media(max-width:479px){
  .ft-nl-input-row{flex-direction:column;border-radius:var(--nh-r);}
  .ft-nl-btn{width:100%;border-radius:0;}
  .ft-nl-input{padding:12px 14px;}
}

/* ════════════════════════════════════════
   MAIN GRID
════════════════════════════════════════ */
.ft-main{background:var(--ft-main-bg);padding:clamp(40px,6vw,68px) 0 clamp(28px,4vw,48px);}

.ft-main-grid{
  display:grid;
  grid-template-columns:2fr 1fr 1fr 1fr 1fr;
  gap:clamp(20px,3vw,52px);
  align-items:start;
}
@media(max-width:1199px){
  .ft-main-grid{grid-template-columns:1.6fr 1fr 1fr 1fr;}
}
@media(max-width:899px){
  .ft-main-grid{grid-template-columns:1fr 1fr;gap:28px 20px;}
  .ft-brand-col{grid-column:1/-1;}
}
@media(max-width:479px){
  .ft-main-grid{grid-template-columns:1fr 1fr;gap:24px 14px;}
  .ft-brand-col{grid-column:1/-1;}
}

/* ── Brand ── */
.ft-brand-col{display:flex;flex-direction:column;gap:0;}
.ft-brand{
  display:inline-flex;align-items:center;gap:8px;
  text-decoration:none;color:var(--nh-ink);
  margin-bottom:14px;flex-shrink:0;
}
.ft-brand:hover .ft-brand-name{color:var(--nh-red);}
.ft-logo{display:block;flex-shrink:0;}
.ft-brand-name{
  font-size:1.1875rem;font-weight:800;
  letter-spacing:-.03em;line-height:1;
  color:var(--nh-ink);white-space:nowrap;
  transition:color var(--nh-t);
  font-family:var(--nh-font);
}
.ft-brand-num{color:var(--nh-red);}

.ft-brand-desc{
  font-size:0.8125rem;line-height:1.7;
  color:var(--nh-ink2);margin:0 0 18px;
}

/* Socials — matches header nh-social */
.ft-socials{display:flex;flex-wrap:wrap;gap:7px;margin-bottom:20px;}
.ft-social{
  width:34px;height:34px;border-radius:8px;
  border:1px solid var(--nh-border);
  background:var(--nh-bg);color:var(--nh-ink2);
  display:flex;align-items:center;justify-content:center;
  font-size:0.8125rem;text-decoration:none;
  transition:border-color var(--nh-t),background var(--nh-t),color var(--nh-t),transform .12s;
}
.ft-social:hover{
  border-color:var(--nh-red);background:#fef2f2;color:var(--nh-red);
  transform:translateY(-2px);
}

/* App badges */
.ft-app-badges{display:flex;flex-wrap:wrap;gap:8px;}
.ft-app-badge{
  display:flex;align-items:center;gap:9px;
  padding:8px 13px;border-radius:9px;
  border:1px solid var(--nh-border);background:var(--nh-bg2);
  text-decoration:none;color:var(--nh-ink);
  transition:border-color var(--nh-t),background var(--nh-t),transform .12s;flex-shrink:0;
}
.ft-app-badge:hover{border-color:var(--nh-ink);background:#fff;transform:translateY(-1px);}
.ft-app-badge>.bi{font-size:1.25rem;flex-shrink:0;}
.ft-app-badge-text{display:flex;flex-direction:column;line-height:1.15;}
.ft-app-small{font-size:0.55rem;color:var(--nh-ink2);text-transform:uppercase;letter-spacing:.05em;}
.ft-app-big{font-size:0.8125rem;font-weight:700;}
@media(max-width:399px){.ft-app-badges{display:none;}}

/* ── Nav columns ── */
.ft-col-heading{
  font-size:0.62rem;font-weight:700;
  letter-spacing:.09em;text-transform:uppercase;
  color:var(--nh-ink2);margin:0 0 14px;
  font-family:var(--nh-font);
}

.ft-nav-list{list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:1px;}

/* nav link — expanding red underline on hover, same as dnav-link::after */
.ft-nav-link{
  display:inline-flex;align-items:center;
  font-size:0.8125rem;font-weight:500;
  color:var(--nh-ink2);text-decoration:none;
  padding:5px 0;
  transition:color var(--nh-t);
}
.ft-nav-link::before{
  content:'';display:inline-block;
  width:0;height:1.5px;background:var(--nh-red);
  margin-right:0;flex-shrink:0;vertical-align:middle;
  transition:width var(--nh-t),margin-right var(--nh-t);
}
.ft-nav-link:hover{color:var(--nh-red);}
.ft-nav-link:hover::before{width:10px;margin-right:6px;}
.ft-nav-link.active{color:var(--nh-red);}

/* Topic chips — same pattern as nh-schip in drawer */
.ft-topics{display:flex;flex-wrap:wrap;gap:6px;}
.ft-topic-chip{
  display:inline-block;
  font-size:0.7rem;font-weight:600;
  color:var(--nh-ink2);background:var(--nh-bg2);
  border:1px solid var(--nh-border);
  padding:5px 11px;border-radius:100px;
  text-decoration:none;
  transition:border-color var(--nh-t),color var(--nh-t),background var(--nh-t);
}
.ft-topic-chip:hover{color:var(--nh-red);border-color:rgba(230,51,17,.3);background:#fef2f2;}

/* Contact list */
.ft-contact-list{list-style:none;margin:0 0 12px;padding:0;display:flex;flex-direction:column;gap:10px;}
.ft-contact-list li{display:flex;align-items:flex-start;gap:9px;font-size:0.8rem;color:var(--nh-ink2);line-height:1.55;}
.ft-contact-icon{color:var(--nh-red);flex-shrink:0;margin-top:2px;font-size:0.875rem;}

/* Live badge — same pulsing dot pattern as strip badge */
.ft-live-badge{
  display:inline-flex;align-items:center;gap:7px;
  background:rgba(230,51,17,.07);border:1px solid rgba(230,51,17,.15);
  color:var(--nh-red);font-size:0.68rem;font-weight:700;letter-spacing:.03em;
  padding:5px 11px;border-radius:100px;
  font-family:var(--nh-font);
}
.ft-live-dot{
  width:6px;height:6px;border-radius:50%;
  background:var(--nh-red);flex-shrink:0;
  animation:ft-blink 1.5s ease-in-out infinite;
}

/* ════════════════════════════════════════
   DIVIDER — red lead line
════════════════════════════════════════ */
.ft-divider{
  height:1px;
  background:linear-gradient(to right, var(--nh-red) 40px, var(--nh-border) 40px);
  margin:0;
}
.ft-rtl .ft-divider{background:linear-gradient(to left, var(--nh-red) 40px, var(--nh-border) 40px);}

/* ════════════════════════════════════════
   LEGAL BAR
════════════════════════════════════════ */
.ft-legal-band{background:var(--ft-legal-bg);padding:15px 0;}
.ft-legal-inner{
  display:flex;align-items:center;
  justify-content:space-between;
  flex-wrap:wrap;gap:10px;
}
.ft-copyright{
  font-size:0.74rem;color:rgba(255,255,255,.42);
  margin:0;font-family:var(--nh-font);
  display:flex;align-items:center;gap:4px;flex-wrap:wrap;
}
.ft-copy-sym{color:var(--nh-red);font-size:0.8rem;}
.ft-brand-link{color:rgba(255,255,255,.68);font-weight:700;text-decoration:none;transition:color var(--nh-t);}
.ft-brand-link:hover{color:#fff;}

.ft-legal-links{display:flex;align-items:center;flex-wrap:wrap;}
.ft-legal-link{
  font-size:0.71rem;font-weight:500;
  color:rgba(255,255,255,.38);text-decoration:none;
  padding:4px 10px;
  border-left:1px solid rgba(255,255,255,.11);
  transition:color var(--nh-t);font-family:var(--nh-font);
}
.ft-legal-link:first-child{border-left:none;}
.ft-legal-link:hover{color:rgba(255,255,255,.82);}
.ft-rtl .ft-legal-link{border-left:none;border-right:1px solid rgba(255,255,255,.11);}
.ft-rtl .ft-legal-link:first-child{border-right:none;}

@media(max-width:599px){
  .ft-legal-inner{flex-direction:column;align-items:flex-start;gap:8px;}
  .ft-legal-link{font-size:0.67rem;padding:3px 8px;}
  .ft-legal-link:first-child{padding-left:0;border-left:none;}
}

/* ════════════════════════════════════════
   BACK TO TOP — same nh-btn-solid style
════════════════════════════════════════ */
.ft-btt{
  position:fixed;bottom:28px;right:28px;
  width:40px;height:40px;border-radius:10px;
  background:var(--nh-red);color:#fff;border:none;
  display:flex;align-items:center;justify-content:center;
  font-size:0.9rem;cursor:pointer;
  box-shadow:var(--nh-sh-md);
  opacity:0;transform:translateY(12px);pointer-events:none;
  transition:opacity var(--nh-t),transform var(--nh-t),background var(--nh-t),box-shadow var(--nh-t);
  z-index:900;
}
.ft-btt--show{opacity:1;transform:none;pointer-events:all;}
.ft-btt:hover{background:var(--nh-red-dk);box-shadow:var(--nh-sh-lg);}
.ft-btt:active{transform:scale(.93);}
.ft-rtl .ft-btt{right:auto;left:28px;}
@media(max-width:479px){.ft-btt{bottom:18px;right:16px;}}

/* ════════════════════════════════════════
   MOUNT ANIMATION — staggered fade-in
════════════════════════════════════════ */
.ft-root .ft-nl-band,
.ft-root .ft-main,
.ft-root .ft-legal-band{
  opacity:0;transform:translateY(10px);
  transition:opacity .45s ease,transform .45s ease;
}
.ft-root.ft-in .ft-nl-band   {opacity:1;transform:none;transition-delay:0ms;}
.ft-root.ft-in .ft-main      {opacity:1;transform:none;transition-delay:60ms;}
.ft-root.ft-in .ft-legal-band{opacity:1;transform:none;transition-delay:120ms;}

/* ════════════════════════════════════════
   DARK MODE — exact header dark overrides
════════════════════════════════════════ */
@media(prefers-color-scheme:dark){
  :root{
    --nh-ink:#f1f1f1;
    --nh-ink2:#9ca3af;
    --nh-bg:#111;
    --nh-bg2:#1a1a1a;
    --nh-border:rgba(255,255,255,.09);
    --ft-nl-bg:#0a0a09;
    --ft-main-bg:#111;
    --ft-legal-bg:#0a0a09;
  }
  .ft-social{background:#1a1a1a;}
  .ft-social:hover{background:#1e0f0f;}
  .ft-app-badge{background:#1a1a1a;}
  .ft-app-badge:hover{background:#222;}
  .ft-topic-chip{background:#1a1a1a;}
  .ft-topic-chip:hover{background:#1e0f0f;}
  .ft-nl-input{color:#f1f1f1;}
  .ft-nl-success{background:rgba(22,163,74,.07);}
  .ft-brand-name{color:#f1f1f1;}
}

/* ════════════════════════════════════════
   PRINT
════════════════════════════════════════ */
@media print{
  .ft-nl-band{display:none;}
  .ft-btt{display:none;}
  .ft-root{background:#fff;color:#111;}
  .ft-legal-band{background:#f5f5f5;}
  .ft-copyright,.ft-legal-link{color:#555;}
}
`;

export default Footer;